// "What does N credits actually buy" — a slider over the real rates the
// control plane bills against (GET /pricing.json, this app's own backend,
// which caches the control plane's public GET /api/v1/pricing — see
// README.md). Fetched once on mount, never per slider move.
//
// This component itself is purely "here's what your credits are worth" —
// the actual purchase button lives beside it on the page (Home.tsx), and
// the checkout flow itself in /claim (see server/src/index.js's
// /session/purchase/* routes and README.md's "Credits" section).

import { useEffect, useMemo, useState } from 'react';
import { Spinner } from './ui';
import { CreditsSlider, CREDIT_MILESTONES } from './CreditsSlider';
import { INSTANCE_SIZES, costPerSecond, usePricing } from '../pricing';

const SHARED = INSTANCE_SIZES[0];

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  if (n >= 100) return n.toFixed(0);
  if (n >= 1) return n.toFixed(1);
  return n.toFixed(2);
}

// Months and years as the billing math counts them (730 h / 8,760 h, the
// same as /instances.md and InstancePricing), not calendar ones.
const HOURS_PER_DAY = 24;
const HOURS_PER_MONTH = 730;
const HOURS_PER_YEAR = 8760;

function plural(n: string, unit: string): string {
  return `${n} ${unit}${n === '1' || n === '1.0' ? '' : 's'}`;
}

/** Hours under 2 days, days under a month, months under a year, then years. */
function formatDuration(hours: number): string {
  if (hours < 2 * HOURS_PER_DAY) return plural(formatNumber(hours), 'hour');
  if (hours < HOURS_PER_MONTH) return plural(formatNumber(hours / HOURS_PER_DAY), 'day');
  if (hours < HOURS_PER_YEAR) return plural((hours / HOURS_PER_MONTH).toFixed(1), 'month');
  return plural((hours / HOURS_PER_YEAR).toFixed(1), 'year');
}

export function PricingCalculator() {
  const { pricing, error } = usePricing();
  const [credits, setCredits] = useState(CREDIT_MILESTONES[0]);
  const [pop, setPop] = useState(false);

  useEffect(() => {
    setPop(true);
    const t = setTimeout(() => setPop(false), 220);
    return () => clearTimeout(t);
  }, [credits]);

  const stats = useMemo(() => {
    if (!pricing) return null;
    const containerPerSecond = costPerSecond(pricing, SHARED.cpuCores, SHARED.memoryGb);
    return { containerHours: credits / (containerPerSecond * 3600) };
  }, [pricing, credits]);

  if (error) return null; // The rest of the page works fine without this section.
  if (!pricing || !stats) {
    return (
      <div className="price-calculator">
        <Spinner label="Loading live pricing…" />
      </div>
    );
  }

  return (
    <div className="price-calculator">
      <div className="price-slider-head">
        <div>
          <span className="price-credits-value">
            {credits}
            <span className="unit">credit{credits === 1 ? '' : 's'}</span>
          </span>
        </div>
        <span className="price-usd">= ${(credits * pricing.credit.usd).toFixed(2)}</span>
      </div>

      <CreditsSlider value={credits} onChange={setCredits} />

      <div className="price-stats">
        <div className="price-stat-row">
          <span className="price-stat-label">Always-on {SHARED.name} container</span>
          <span className={`price-stat-value ${pop ? 'pop' : ''}`}>
            {formatDuration(stats.containerHours)}
          </span>
        </div>
      </div>

      <p className="faint small price-footnote">
        Container sized as {SHARED.name}: billed for {SHARED.cpuCores} vCPU / {SHARED.memoryGb}GB{SHARED.burst ? `, using up to ${SHARED.burst.cpuCores} vCPU / ${SHARED.burst.memoryGb}GB when spare` : ''}.
        Rates come straight from{' '}
        <a href="https://cloudish.ai/api" target="_blank" rel="noreferrer">
          the live API
        </a>
        .
      </p>
    </div>
  );
}

export default PricingCalculator;
